import { animate, query, stagger, style, transition, trigger } from '@angular/animations';
import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { ExportChartData } from '@ex/module/core/data-models/export-chart-options';
import { EnpsAnalysisResponse } from '@ex/module/core/services/engagement.service';
import { ChartBaseComponent } from '@ex/module/shared/chart/chart-base.component';
import { JsonExportData, JsonToExcelComponent } from '../export-components/json-to-excel/json-to-excel.component';
import { inOutAnimation, slidesAniamtion, ChildrenAnimation, smallAnimation } from '../animations/allAnimations';
import { DecimalPipe } from '@angular/common';

export const listAnimation = trigger('listAnimation', [
  transition('* <=> *', [
    query(
      ':enter',
      [
        style({ opacity: 0, transform: 'translateY(40px) rotateX(90deg)' }),
        stagger(
          '50ms',
          animate(
            '600ms cubic-bezier(0,.81,.18,1)',
            style({ opacity: 1, transform: 'translateY(0px) rotateX(0deg)' })
          )
        ),
      ],
      { optional: true }
    ),
  ]),
]);

@Component({
  selector: 'enps-chart',
  templateUrl: './enps-chart.component.html',
  styleUrls: ['./enps-chart.component.scss'],
  animations: [
    inOutAnimation,
    slidesAniamtion,
    ChildrenAnimation,
    smallAnimation,
    listAnimation
  ],
  providers:[DecimalPipe]
})
export class EnpsChartComponent extends ChartBaseComponent {
  enpsAnalysis: EnpsAnalysisResponse;
  showBreakdown: boolean = false;
  exportChartData: ExportChartData;
  exportPageRows: number = 13;
  @Output() exportPageLength: EventEmitter<any> = new EventEmitter();
  @Input() export: boolean = false;
  @Input() language: string;
  @Input() criteria: any;
  @Input() criteriaType: string;
  rowView: boolean = false;
  chartExport: boolean = false;
  numberPipe = inject(DecimalPipe);

  @Input('enpsAnalysis') set request(enpsAnalysis: { Data: EnpsAnalysisResponse }) {
    if (!enpsAnalysis?.Data?.ChartData?.length)
      return;

    const roundOf = (v:number) =>  parseFloat(this.numberPipe.transform(v, '1.1-1'))

    enpsAnalysis.Data.OverallPercentage = roundOf(enpsAnalysis.Data.PromotersPercentage) - roundOf(enpsAnalysis.Data.DetractorsPercentage)

    this.enpsAnalysis = { ...enpsAnalysis.Data };

    this.enpsAnalysis.ChartData = this.enpsAnalysis.ChartData
      .filter(x => x.Detractors + x.Passives + x.Promoters >= 3);
    this.enpsAnalysis.ChartData = this.constructSeries();
    this.chartExport = this.export;

    this.exportChartData = {
      customBorders: true,
      reportTitle: this.localize('enpsScore', this.language, this.ModuleNames.Shared) + ' ' + this.localize('txtBy', this.language, this.ModuleNames.Shared) + ' ' + this.ExtractPipe.transform(this.criteria.name, this.language)
    }

    this.exportPageLength.emit(this.calculateExportPages(this.enpsAnalysis.ChartData.length));
  };

  FIRST_PAGE_ROWS = 15;
  SUBSEQUENT_PAGE_ROWS = 25;
  private calculateExportPages(rowCount: number): number {
    if (rowCount <= 0)
      return 0;
    if (rowCount <= this.FIRST_PAGE_ROWS)
      return 1;
    return 1 + Math.ceil((rowCount - this.FIRST_PAGE_ROWS) / this.SUBSEQUENT_PAGE_ROWS);
  }

  constructSeries() {
    return this.enpsAnalysis.ChartData
      .map(x => {
        x['value'] = x.Value;
        x['absValue'] = Math.abs(x.Value);
        x['overall'] = x.Detractors + x.Passives + x.Promoters;

        x['DetractorsPercentage'] = x.Detractors ? (x.Detractors / x['overall']) * 100 : 0;
        x['PassivesPercentage'] = x.Passives ? (x.Passives / x['overall']) * 100 : 0;
        x['PromotersPercentage'] = x.Promoters ? (x.Promoters / x['overall']) * 100 : 0;

        x['itemStyle'] = {
          color: !x.Value ? '#FF9600' : x.Value > 0 ? '#3EB75B' : '#FF4530'
        }
        return x
      })
  }


  downloadChart(id: string) {
    this.LoaderService.displayLoader();
    this.chartExport = true;

    setTimeout(() => {
      this.exportBasePdf(id, this.exportChartData)
    }, 2000);
    setTimeout(() => {
      this.chartExport = false;
      this.LoaderService.hideLoader();

    }, 5000);
  }

  downloadExcel() {
    const data = this.enpsAnalysis.ChartData.map((x: any) => {
      return {
        "Category": x.Category,
        "Promoters": x.Promoters,
        "Passives": x.Passives,
        "Detractors": x.Detractors,
        "Overall": x.overall,
        "PromotersPercentage": this.toFixed(x.PromotersPercentage, 1) + '%',
        "PassivesPercentage": this.toFixed(x.PassivesPercentage, 1) + '%',
        "DetractorsPercentage": this.toFixed(x.DetractorsPercentage, 1) + '%',
        "OverallPercentage": this.toFixed(x.value, 1) + '%',
      }
    });
    this.ZenCDKService.open(JsonToExcelComponent, {
      data,
      name: this.exportChartData.reportTitle,
      includeTitle: true,
      grouping: false,
    } as JsonExportData)
  }
}
